/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid2,
  Stack,
  Typography,
  Link,
} from "@mui/material";
import { PageProps, graphql, navigate } from "gatsby";
import { PortableText } from "@portabletext/react";
import urlBuilder from "@sanity/image-url";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigationWrapper from "../components/layout/NavigationWrapper";
import SEO from "../components/Seo";
import BlogPostImage from "../components/sections/Article/BlogPostImage";
import sanity from "../constants/sanity";
import COLORS from "../constants/colors";
import RelatedArticles from "../components/sections/Article/RelatedArticles";
import {
  getArticleCategory,
} from "../utils/sanity";
import { BlogPost } from "../interfaces/sanity/legacySanityClasses";
import { buildImageObj, imageUrlFor } from "../lib/helpers";
import { BaseUrl } from "../constants/url";
import { mapAlignmentToAlignItems } from "../components/sanity/portableText/configurations";


export const query = graphql`
  query PostQuery($id: String!, $catId: String!) {
    sanityPost(id: { eq: $id }) {
      title
      _id
      slug {
        current
      }
      author {
        id
        name
        bio {
          children {
            text
          }
        }
        image {
          asset {
            _id
          }
        }
      }
      publishedAt(formatString: "DD. MMMM YYYY", locale: "nb-NO")
      mainImage {
        asset {
          _id
        }
      }
      _rawBody
      excerpt
      seoKeywords
      seoTitle
      seoMainImage {
        asset {
          _id
        }
      }
      categories {
        title
        description
        id
      }
    }
    allSanityPost(
      filter: {
        categories: { elemMatch: { id: { eq: $catId } } }
        id: { ne: $id }
      }
      limit: 3
    ) {
      edges {
        node {
          id
          mainImage {
            asset {
              _id
            }
          }
          title
          slug {
            current
          }
          excerpt
          previewImage {
            asset {
              _id
            }
          }
          categories {
            id
            _id
            title
            description
          }
        }
      }
    }
  }
`;

const BloggPostTemplate = ({ data }: PageProps<Queries.PostQueryQuery>) => {
  const components = {
    block: {
      // eslint-disable-next-line
      normal: ({ children }: any) => (
        <Typography gutterBottom>{children}</Typography>
      ),
      // eslint-disable-next-line
      h1: ({ children }: any) => (
        <Typography variant="h2" paddingBottom="24px">
          {children}
        </Typography>
      ),
      // eslint-disable-next-line
      h2: ({ children }: any) => (
        <Typography variant="h2" gutterBottom>
          {children}
        </Typography>
      ),
      // eslint-disable-next-line
      h3: ({ children }: any) => (
        <Typography variant="h3" gutterBottom>
          {children}
        </Typography>
      ),
      // eslint-disable-next-line
      h4: ({ children }: any) => (
        <Typography variant="h3" paddingBottom="24px">
          {children}
        </Typography>
      ),
    },
    types: {
      // eslint-disable-next-line
      image: (props: any) => (
        <BlogPostImage title={data.sanityPost?.title} {...props} />
      ),
      youtubeEmbed: ({ value: { url } }: any) => {
        return (
          <Box py="12px">
            <iframe
              style={{
                border: "0px",
                aspectRatio: "16 / 9",
                boxShadow: "0px 4px 60px 0px rgba(15, 11, 54, 0.25)",
              }}
              width="100%"
              height="100%"
              src={url}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;"
              allowFullScreen
            />
          </Box>
        );
      },
      interestExample: ({ value: { alignment } }) => {
        return (
          <Stack>
            <Typography
              color={COLORS.BLACK[100]}
              fontWeight={600}
              variant="bodySmall"
              textAlign={alignment}
            >
              Renteeksempel refinansering
            </Typography>
            <Typography
              color={COLORS.BLACK[100]}
              variant="bodySmall"
              paragraph
              textAlign={alignment}
            >
              135&nbsp;000&nbsp;kr over 8&nbsp;år, effektiv rente 14,73%,
              kostnad 85&nbsp;376&nbsp;kr, totalt 220&nbsp;376&nbsp;kr
            </Typography>
          </Stack>
        );
      },
      linkButton: ({ value: { text, url, variant, alignment } }) => {
        return (
          <Stack
            py="8px"
            alignItems={mapAlignmentToAlignItems(alignment)}
            alignContent="flex-start"
          >
            <Button
              variant={variant}
              color="dark"
              onClick={() => navigate(url)}
            >
              {text}
            </Button>
          </Stack>
        );
      },
    },
  };

  return (
    <NavigationWrapper>
      <Stack component="article">
        <Stack gap="50px">
          <Stack bgcolor={COLORS.DARK_BLUE[100]}>
            <Container>
              <Stack
                gap={{ xs: "48px", md: "84px" }}
                pt={{ xs: "24px", md: "84px" }}
                pb={{ xs: "48px", md: "110px" }}
              >
                <Stack>
                  <Stack alignItems="flex-start">
                    <Button
                      variant="text"
                      color="light"
                      LinkComponent="a"
                      startIcon={<ArrowBackIcon />}
                      href="/artikler" // for SEO
                    >
                      Tilbake
                    </Button>
                  </Stack>
                </Stack>
                <Grid2 container spacing="24px">
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Stack
                      gap={{ xs: "24px", md: "32px" }}
                      alignItems="flex-start"
                    >
                      <Link href="/artikler" style={{ textDecoration: "none" }}>
                        <Chip
                          label={getArticleCategory(data.sanityPost)}
                          color="secondary"
                        />
                      </Link>
                      <Typography
                        color={COLORS.WHITE[100]}
                        variant="h1Article"
                        component="h1"
                        sx={{
                          fontSize: { xs: "24px", md: "46px" },
                          paddingTop: "9px",
                          paddingBottom: "6px",
                          overflow: "hidden",
                          display: "-webkit-box",
                          "-webkit-line-clamp": "2",
                          "-webkit-box-orient": "vertical",
                        }}
                      >
                        {data.sanityPost?.title}
                      </Typography>
                      <Typography
                        color={COLORS.WHITE[100]}
                        variant="lead"
                        sx={{
                          overflow: "hidden",
                          display: "-webkit-box",
                          "-webkit-line-clamp": "none",
                          "-webkit-box-orient": "vertical",
                        }}
                      >
                        {data.sanityPost?.excerpt}
                      </Typography>
                      <Typography
                        color={COLORS.WHITE[100]}
                        variant="articleAuthor"
                        sx={{
                          fontWeight: "medium",
                          overflow: "hidden",
                          display: "-webkit-box",
                          "-webkit-line-clamp": "none",
                          "-webkit-box-orient": "vertical",
                        }}
                      >
                        Publisert {data.sanityPost?.publishedAt} •{" "}
                        {data.sanityPost?.author?.name},{" "}
                        {data.sanityPost?.author?.bio[0]?.children[0]?.text ??
                          ""}
                      </Typography>
                    </Stack>
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }} justifyContent="center">
                    <Stack
                      component="img"
                      width="100%"
                      height="100%"
                      maxWidth="610px"
                      maxHeight="460px"
                      sx={{
                        aspectRatio: "610/460",
                        objectFit: "cover",
                        borderRadius: "20px",
                      }}
                      src={urlBuilder()
                        .image(data.sanityPost?.mainImage ?? "")
                        .projectId(sanity.PROJECT_ID)
                        .dataset("blogg")
                        .fit("fill")
                        .format("webp")
                        .auto("format")
                        .quality(100)
                        .width(610)
                        .height(460)
                        .url()}
                      alt={data.sanityPost?.title ?? ""}
                    />
                  </Grid2>
                </Grid2>
              </Stack>
            </Container>
          </Stack>
          <Container maxWidth="md">
            <Stack
              component="section"
              mb="80px"
              fontWeight="light"
              lineHeight="2"
              pt="30px"
            >
              <PortableText
                value={data.sanityPost?._rawBody}
                components={components}
              />
            </Stack>
          </Container>
        </Stack>
      </Stack>
      {data.allSanityPost.edges.length > 0 && (
        <Stack bgcolor={COLORS.WHITE[100]} py="40px">
          <Container>
            <Stack gap="30px">
              <Stack>
                <Typography variant="h2" color={COLORS.DARK_BLUE[100]}>
                  Relaterte artikler
                </Typography>
              </Stack>
              <Stack>
                <RelatedArticles relatedArticles={data.allSanityPost} />
              </Stack>
            </Stack>
          </Container>
        </Stack>
      )}
    </NavigationWrapper>
  );
};

export default BloggPostTemplate;

export function Head(props: BlogPost) {
  // eslint-disable-next-line react/destructuring-assignment, @typescript-eslint/no-shadow
  const sanity = props.data.sanityPost;
  const { location } = props;

  const image = sanity.seoMainImage ?? sanity.mainImage;

  return (
    <SEO
      title={`${sanity.seoTitle ?? sanity.title}`}
      description={sanity.excerpt}
      keywords={sanity.seoKeywords}
      image={image ? imageUrlFor(buildImageObj(image)).url() : undefined}
      canonical={BaseUrl + location.pathname}
      categories={sanity.categories}
      structuredDataArticle={{
        author: sanity?.author?.name,
        datePublished: sanity?.publishedAt,
      }}
    />
  );
}
